import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "./AdminCustomRequests.css";
import getApiBase from "../utils/apiBase";
import { resolveImageUrl } from "../utils/resolveImageUrl";
import Seo from "../components/Seo";

const API_BASE = getApiBase();
const STATUSES = [
  "awaiting_upload",
  "submitted",
  "new",
  "reviewing",
  "confirmed",
  "quoted",
  "deposit_paid",
  "in_production",
  "completed",
  "cancelled",
];

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const notificationLabel = (notifications, sentKey, errorKey) => {
  if (notifications?.[sentKey]) return "Sent";
  const error = notifications?.[errorKey] || "";
  if (/disabled|missing|configuration/i.test(error)) return "Disabled";
  if (error) return "Failed";
  return "Pending";
};

const AdminCustomRequests = () => {
  const [auth, setAuth] = useState(localStorage.getItem("adminAuth") || "");
  const [login, setLogin] = useState({ username: "", password: "" });
  const [requests, setRequests] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [draft, setDraft] = useState({ status: "new", adminNote: "" });
  const [statusMessage, setStatusMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notifying, setNotifying] = useState("");

  const authHeaders = useMemo(
    () => (auth ? { Authorization: `Basic ${auth}` } : {}),
    [auth],
  );

  const selectedRequest = useMemo(
    () => requests.find((item) => item.id === selectedId) || requests[0] || null,
    [requests, selectedId],
  );

  const fetchRequests = useCallback(async () => {
    if (!auth) return;
    setLoading(true);
    setStatusMessage("");
    try {
      const response = await fetch(`${API_BASE}/api/admin/custom-requests`, {
        headers: authHeaders,
      });
      if (!response.ok) {
        throw new Error("Failed to load custom requests.");
      }
      const data = await response.json();
      const items = Array.isArray(data?.items) ? data.items : [];
      setRequests(items);
      setSelectedId((current) => {
        const queryId = new URLSearchParams(window.location.search).get("requestId");
        return current || queryId || items[0]?.id || "";
      });
    } catch (error) {
      setStatusMessage("Failed to load custom requests.");
    } finally {
      setLoading(false);
    }
  }, [auth, authHeaders]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  useEffect(() => {
    if (selectedRequest) {
      setDraft({
        status: selectedRequest.status || "new",
        adminNote: selectedRequest.adminNote || "",
      });
    }
  }, [selectedRequest]);

  const handleLogin = (event) => {
    event.preventDefault();
    const token = btoa(`${login.username}:${login.password}`);
    localStorage.setItem("adminAuth", token);
    setAuth(token);
    setStatusMessage("Logged in.");
  };

  const handleLogout = () => {
    localStorage.removeItem("adminAuth");
    setAuth("");
    setLogin({ username: "", password: "" });
    setRequests([]);
    setSelectedId("");
    setStatusMessage("Logged out.");
  };

  const saveRequest = async () => {
    if (!selectedRequest) return;
    setSaving(true);
    setStatusMessage("");
    try {
      const response = await fetch(`${API_BASE}/api/admin/custom-requests/${selectedRequest.id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
        },
        body: JSON.stringify(draft),
      });
      if (!response.ok) {
        throw new Error("Failed to update custom request.");
      }
      const data = await response.json();
      const updated = data.request;
      setRequests((current) => current.map((item) => (
        item.id === updated.id ? updated : item
      )));
      setStatusMessage("Custom request updated.");
    } catch (error) {
      setStatusMessage("Failed to update custom request.");
    } finally {
      setSaving(false);
    }
  };

  const resendNotification = async (type) => {
    if (!selectedRequest || notifying) return;
    setNotifying(type);
    setStatusMessage("");
    try {
      const response = await fetch(`${API_BASE}/api/admin/custom-requests/${selectedRequest.id}/notify`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
        },
        body: JSON.stringify({ type }),
      });
      if (!response.ok) {
        throw new Error("Failed to resend notification.");
      }
      const data = await response.json();
      const updated = data.request;
      setRequests((current) => current.map((item) => (
        item.id === updated.id ? updated : item
      )));
      setStatusMessage(data.result?.success ? "Notification sent." : data.result?.error || "Notification failed.");
    } catch (error) {
      setStatusMessage("Failed to resend notification.");
    } finally {
      setNotifying("");
    }
  };

  return (
    <main className="admin-custom-requests">
      <Seo
        title="Admin Custom Requests"
        description="Internal custom request management page."
        path="/admin/custom-requests"
        noindex
      />
      <header className="admin-custom-header">
        <div>
          <h1>Custom Requests</h1>
          <Link to="/admin">Product Admin</Link>
          <Link to="/admin/orders">Orders</Link>
          <Link to="/admin/custom-projects">Custom Design Projects</Link>
        </div>
        {auth ? <button type="button" onClick={handleLogout}>Log out</button> : null}
      </header>

      {!auth && (
        <form className="admin-custom-login" onSubmit={handleLogin}>
          <h2>Admin Login</h2>
          <label>
            Username
            <input
              type="text"
              value={login.username}
              onChange={(event) => setLogin((current) => ({ ...current, username: event.target.value }))}
            />
          </label>
          <label>
            Password
            <input
              type="password"
              value={login.password}
              onChange={(event) => setLogin((current) => ({ ...current, password: event.target.value }))}
            />
          </label>
          <button type="submit">Log in</button>
        </form>
      )}

      {auth && (
        <section className="admin-custom-layout">
          <aside className="admin-custom-list">
            <div className="admin-custom-list-header">
              <h2>Requests</h2>
              <button type="button" onClick={fetchRequests} disabled={loading}>
                {loading ? "Loading..." : "Refresh"}
              </button>
            </div>
            {requests.length ? (
              requests.map((requestItem) => (
                <button
                  type="button"
                  className={`request-list-item${selectedRequest?.id === requestItem.id ? " selected" : ""}`}
                  key={requestItem.id}
                  onClick={() => setSelectedId(requestItem.id)}
                >
                  <strong>{requestItem.name || "Unnamed customer"}</strong>
                  <span>{requestItem.productName}</span>
                  <em>{requestItem.status}</em>
                  <small>{formatDate(requestItem.createdAt)}</small>
                  <span className="notification-summary">
                    WeCom: {notificationLabel(requestItem.notifications, "weworkSent", "lastWeworkError")}
                  </span>
                  <span className="notification-summary">
                    Customer Email: {notificationLabel(requestItem.notifications, "customerEmailSent", "lastCustomerEmailError")}
                  </span>
                  <span className="notification-summary">
                    Internal Email: {notificationLabel(requestItem.notifications, "internalEmailSent", "lastInternalEmailError")}
                  </span>
                </button>
              ))
            ) : (
              <p className="admin-empty-state">No custom requests yet.</p>
            )}
          </aside>

          <section className="admin-custom-detail">
            {selectedRequest ? (
              <>
                <div className="request-detail-heading">
                  <div>
                    <p>Request ID</p>
                    <h2>{selectedRequest.id}</h2>
                  </div>
                  <span className={`status-pill status-${selectedRequest.status || "new"}`}>
                    {selectedRequest.status || "new"}
                  </span>
                </div>

                <div className="request-fields">
                  <div>
                    <span>Customer name</span>
                    <strong>{selectedRequest.name}</strong>
                  </div>
                  <div>
                    <span>Email</span>
                    <a href={`mailto:${selectedRequest.email}`}>{selectedRequest.email}</a>
                  </div>
                  <div>
                    <span>Country</span>
                    <strong>{selectedRequest.country}</strong>
                  </div>
                  <div>
                    <span>Selected product/model</span>
                    <strong>{selectedRequest.productName}</strong>
                  </div>
                  <div>
                    <span>Selected label color</span>
                    <strong>{selectedRequest.selectedColor || "Not set"}</strong>
                  </div>
                  <div>
                    <span>Quantity</span>
                    <strong>{selectedRequest.quantity}</strong>
                  </div>
                  <div>
                    <span>Created time</span>
                    <strong>{formatDate(selectedRequest.createdAt)}</strong>
                  </div>
                </div>

                <div className="request-media-grid">
                  <figure>
                    {selectedRequest.artworkUrl ? (
                      <img src={resolveImageUrl(selectedRequest.artworkUrl)} alt="Uploaded artwork" />
                    ) : (
                      <div className="request-media-placeholder">Awaiting artwork upload</div>
                    )}
                    <figcaption>
                      Uploaded artwork
                      {selectedRequest.artworkUrl ? <a href={resolveImageUrl(selectedRequest.artworkUrl)} download>
                        Download
                      </a> : null}
                    </figcaption>
                  </figure>
                  <figure>
                    <img src={resolveImageUrl(selectedRequest.previewImageUrl)} alt="Generated custom yo-yo preview" />
                    <figcaption>
                      Generated preview image
                      <a href={resolveImageUrl(selectedRequest.previewImageUrl)} download>
                        Download
                      </a>
                    </figcaption>
                  </figure>
                </div>

                <div className="request-notes">
                  <h3>Customer notes</h3>
                  <p>{selectedRequest.notes || "No notes provided."}</p>
                </div>

                <div className="request-notifications">
                  <h3>Notifications</h3>
                  <div className="notification-grid">
                    <div>
                      <span>WeCom</span>
                      <strong>{notificationLabel(selectedRequest.notifications, "weworkSent", "lastWeworkError")}</strong>
                      <small>{formatDate(selectedRequest.notifications?.weworkSentAt)}</small>
                      <p>{selectedRequest.notifications?.lastWeworkError || ""}</p>
                    </div>
                    <div>
                      <span>Customer Email</span>
                      <strong>{notificationLabel(selectedRequest.notifications, "customerEmailSent", "lastCustomerEmailError")}</strong>
                      <small>{formatDate(selectedRequest.notifications?.customerEmailSentAt)}</small>
                      <p>{selectedRequest.notifications?.lastCustomerEmailError || ""}</p>
                    </div>
                    <div>
                      <span>Internal Email</span>
                      <strong>{notificationLabel(selectedRequest.notifications, "internalEmailSent", "lastInternalEmailError")}</strong>
                      <small>{formatDate(selectedRequest.notifications?.internalEmailSentAt)}</small>
                      <p>{selectedRequest.notifications?.lastInternalEmailError || ""}</p>
                    </div>
                  </div>
                  {selectedRequest.notifications?.errors?.length ? (
                    <ul className="notification-errors">
                      {selectedRequest.notifications.errors.map((errorItem, index) => (
                        <li key={`${errorItem.createdAt}-${index}`}>
                          <strong>{errorItem.type}</strong>
                          <span>{formatDate(errorItem.createdAt)}</span>
                          <p>{errorItem.message}</p>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                  <div className="notification-actions">
                    <button type="button" onClick={() => resendNotification("wework")} disabled={Boolean(notifying)}>
                      {notifying === "wework" ? "Sending..." : "Resend WeCom Notification"}
                    </button>
                    <button type="button" onClick={() => resendNotification("internal_email")} disabled={Boolean(notifying)}>
                      {notifying === "internal_email" ? "Sending..." : "Resend Internal Email"}
                    </button>
                    <button type="button" onClick={() => resendNotification("customer_email")} disabled={Boolean(notifying)}>
                      {notifying === "customer_email" ? "Sending..." : "Resend Customer Email"}
                    </button>
                  </div>
                </div>

                <div className="request-admin-editor">
                  <label>
                    Status
                    <select
                      value={draft.status}
                      onChange={(event) => setDraft((current) => ({ ...current, status: event.target.value }))}
                    >
                      {STATUSES.map((status) => (
                        <option value={status} key={status}>{status}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Internal note
                    <textarea
                      rows="6"
                      value={draft.adminNote}
                      onChange={(event) => setDraft((current) => ({ ...current, adminNote: event.target.value }))}
                    />
                  </label>
                  <button type="button" onClick={saveRequest} disabled={saving}>
                    {saving ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </>
            ) : (
              <p className="admin-empty-state">Select a request to view details.</p>
            )}
          </section>
        </section>
      )}

      {statusMessage && <p className="admin-custom-status">{statusMessage}</p>}
    </main>
  );
};

export default AdminCustomRequests;
